import React, { useState } from "react";
import { useTranslation } from "react-i18next";

interface InvitationPreviewModalProps {
  recipientEmail: string;
  recipientName?: string;
  defaultMessage: string;
  inviterName: string;
  inviterEmail: string;
  systemEmail: string;
  inviteType: "project" | "uploader" | "redirect";
  projectTitle?: string;   // project name (project/uploader) or request title (redirect)
  onSend: (customMessage: string) => void;
  onCancel: () => void;
}

/** Build the pre-filled body text that mirrors the backend default template. */
function buildDefaultBody(
  t: (key: string, options?: Record<string, unknown>) => string,
  inviteType: "project" | "uploader" | "redirect",
  recipientEmail: string,
  recipientName: string | undefined,
  inviterName: string,
  projectTitle: string | undefined  // project name for project/uploader; request title for redirect
): string {
  const greeting = recipientName
    ? t("body.greetingWithName", { name: recipientName })
    : t("body.greetingWithEmail", { email: recipientEmail });

  if (inviteType === "project") {
    return (
      `${greeting}\n\n` +
      t("body.project", { project: projectTitle || "", inviter: inviterName })
    );
  }

  if (inviteType === "redirect") {
    return t("body.redirect", { inviter: inviterName, title: projectTitle || "" });
  }

  return (
    `${greeting}\n\n` +
    t("body.uploader", { inviter: inviterName, project: projectTitle || "" })
  );
}

export const InvitationPreviewModal: React.FC<InvitationPreviewModalProps> = ({
  recipientEmail,
  recipientName,
  defaultMessage,
  inviterName,
  inviterEmail,
  systemEmail,
  inviteType,
  projectTitle,
  onSend,
  onCancel,
}) => {
  const { t } = useTranslation(["invitationPreviewModal", "common"]);
  const prefilled = defaultMessage || buildDefaultBody(
    t, inviteType, recipientEmail, recipientName, inviterName, projectTitle
  );
  const [message, setMessage] = useState(prefilled);

  const headerColor =
    inviteType === "project" ? "#6f42c1" :
    inviteType === "redirect" ? "#f59e0b" : "#fd7e14";
  const subject =
    inviteType === "project"
      ? t("subject.project", { project: projectTitle || "" })
      : inviteType === "redirect"
      ? t("subject.redirect")
      : projectTitle
      ? t("subject.uploaderWithProject", { project: projectTitle })
      : t("subject.uploaderNoProject");
  const actionLabel =
    inviteType === "project" ? t("actionLabel.project") :
    inviteType === "redirect" ? t("actionLabel.redirect") : t("actionLabel.uploader");

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: "#f5f5f5",
          borderRadius: "8px",
          width: "680px",
          maxWidth: "95vw",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "14px 20px",
            background: "white",
            borderBottom: "1px solid #dee2e6",
            borderRadius: "8px 8px 0 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "15px", color: "#495057" }}>
            {t("header")}
          </h3>
          <button
            onClick={onCancel}
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#6c757d",
              padding: "0 4px",
            }}
          >
            ×
          </button>
        </div>

        {/* Envelope info bar */}
        <div
          style={{
            padding: "12px 20px",
            background: "white",
            borderBottom: "1px solid #e9ecef",
            fontSize: "13px",
            lineHeight: "1.8",
          }}
        >
          <div>
            <span style={{ fontWeight: 600, color: "#495057", display: "inline-block", width: "60px" }}>{t("envelope.from")}</span>
            <span style={{ color: "#6c757d" }}>{systemEmail}</span>
          </div>
          <div>
            <span style={{ fontWeight: 600, color: "#495057", display: "inline-block", width: "60px" }}>{t("envelope.to")}</span>
            <span>{recipientEmail}</span>
          </div>
          <div>
            <span style={{ fontWeight: 600, color: "#495057", display: "inline-block", width: "60px" }}>{t("envelope.cc")}</span>
            <span style={{ color: "#6c757d" }}>{inviterEmail}</span>
            <span style={{ fontSize: "11px", color: "#adb5bd", marginLeft: "6px" }}>{t("envelope.ccNotice")}</span>
          </div>
          <div>
            <span style={{ fontWeight: 600, color: "#495057", display: "inline-block", width: "60px" }}>{t("envelope.subject")}</span>
            <span>{subject}</span>
          </div>
        </div>

        {/* Email preview */}
        <div style={{ padding: "20px" }}>
          <div
            style={{
              maxWidth: "560px",
              margin: "0 auto",
              fontFamily: "Arial, sans-serif",
              color: "#333",
              background: "white",
              borderRadius: "5px",
              overflow: "hidden",
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            }}
          >
            {/* Email header banner */}
            <div
              style={{
                background: headerColor,
                color: "white",
                padding: "20px",
                textAlign: "center",
                borderRadius: "5px 5px 0 0",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 600 }}>{subject}</h2>
            </div>

            {/* Email body */}
            <div
              style={{
                padding: "24px",
                background: "#f9f9f9",
                border: "1px solid #ddd",
                borderTop: "none",
                borderRadius: "0 0 5px 5px",
              }}
            >
              {/* Summary bar (fixed) */}
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#555",
                  marginBottom: "16px",
                  padding: "12px",
                  background: "#e8f4f8",
                  borderLeft: `4px solid ${headerColor}`,
                  borderRadius: "0 4px 4px 0",
                }}
              >
                {inviteType === "project"
                  ? t("summary.project", { inviter: inviterName, project: projectTitle || "" })
                  : inviteType === "redirect"
                  ? t("summary.redirect", { inviter: inviterName, title: projectTitle || "" })
                  : projectTitle
                  ? t("summary.uploaderWithProject", { inviter: inviterName, project: projectTitle })
                  : t("summary.uploaderNoProject", { inviter: inviterName })}
              </div>

              {/* Editable body — full email text */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "11px",
                    color: "#adb5bd",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: "6px",
                  }}
                >
                  {t("editableBodyLabel")}
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={10}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: `1px solid ${headerColor}55`,
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontFamily: "Arial, sans-serif",
                    lineHeight: "1.6",
                    resize: "vertical",
                    boxSizing: "border-box",
                    background: "#fffff8",
                    color: "#333",
                    outline: "none",
                  }}
                />
              </div>

              {/* Action button preview (fixed) */}
              <div style={{ textAlign: "center", margin: "20px 0" }}>
                <span
                  style={{
                    display: "inline-block",
                    padding: "12px 30px",
                    background: headerColor,
                    color: "white",
                    borderRadius: "5px",
                    fontWeight: 600,
                    fontSize: "15px",
                  }}
                >
                  {actionLabel}
                </span>
              </div>

              {/* Footer (fixed) */}
              <div
                style={{
                  textAlign: "center",
                  paddingTop: "16px",
                  borderTop: "1px solid #eee",
                  color: "#999",
                  fontSize: "11px",
                }}
              >
                {t("footer.serviceName")}
                <br />
                {t("footer.automatedNotice")}
              </div>
            </div>
          </div>
        </div>

        {/* Footer buttons */}
        <div
          style={{
            padding: "12px 20px",
            background: "white",
            borderTop: "1px solid #dee2e6",
            borderRadius: "0 0 8px 8px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            onClick={() => setMessage(prefilled)}
            style={{
              padding: "6px 14px",
              background: "white",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "13px",
              color: "#6c757d",
            }}
          >
            {t("resetToDefault")}
          </button>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={onCancel}
              style={{
                padding: "8px 20px",
                background: "white",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                color: "#495057",
              }}
            >
              {t("common:actions.cancel")}
            </button>
            <button
              onClick={() => onSend(message)}
              style={{
                padding: "8px 20px",
                background: "#28a745",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              {t("sendButton")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
